import React from 'react';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import ContactPortfolioSectionCard from './ContactPortfolioSectionCard';
import type { PortfolioTagsState } from './contactPortfolioTypes';

export default function ContactPortfolioTagsSection({
  tags,
  loading,
}: {
  tags: PortfolioTagsState | null;
  loading: boolean;
}) {
  const hasTags =
    tags && (Object.keys(tags.descriptive).length > 0 || Object.keys(tags.calculated).length > 0);

  if (!loading && !hasTags) return null;

  return (
    <ContactPortfolioSectionCard
      title="Portfolio Tags"
      icon={<LocalOfferIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
      loading={loading}
    >
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {!loading && tags && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Object.values(tags.calculated)
            .filter((t) => t.value != null)
            .map((tag) => (
              <Box
                key={tag.tagId}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {tag.label}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {tag.unit === 'MONTHS'
                    ? `${(tag.value as number).toFixed(1)} months`
                    : tag.unit === 'PERCENTAGE'
                      ? `${(tag.value as number).toFixed(1)}%`
                      : String(tag.value)}
                </Typography>
              </Box>
            ))}

          {Object.values(tags.descriptive)
            .filter((t) => t.values && t.values.length > 0)
            .map((tag) => (
              <Box key={tag.tagId}>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    mb: 0.75,
                    display: 'block',
                  }}
                >
                  {tag.label}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {tag.values!.map((v) => (
                    <Tooltip
                      key={v.value}
                      title={`${v.fundCount} fund${v.fundCount !== 1 ? 's' : ''}`}
                    >
                      <Chip label={v.label} size="small" variant="outlined" />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            ))}
        </Box>
      )}
    </ContactPortfolioSectionCard>
  );
}
